import { useState } from 'react';
import { useAuth } from '@/auth/useAuth';
import { useChangePassword } from '@/features/users';
import { useNotifications } from '@/providers/notificationHooks';
import { useMutationErrorSnackbar } from '@/shared/hooks';
import type { AuthSession } from '@/auth/types';

const strongPasswordRegex = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d).{8,}$/;

export type PasswordChangeFormState = {
  currentPassword: string;
  newPassword: string;
  confirmPassword: string;
  showCurrentPassword: boolean;
  showNewPassword: boolean;
  showConfirmPassword: boolean;
  currentPasswordError: boolean;
  currentPasswordHelperText?: string;
  newPasswordError: boolean;
  newPasswordHelperText?: string;
  confirmPasswordError: boolean;
  confirmPasswordHelperText?: string;
  saveDisabled: boolean;
  saving: boolean;
  successMessage: string | null;
  onCurrentPasswordChange: (value: string) => void;
  onNewPasswordChange: (value: string) => void;
  onConfirmPasswordChange: (value: string) => void;
  onToggleCurrentPassword: () => void;
  onToggleNewPassword: () => void;
  onToggleConfirmPassword: () => void;
  onSave: () => void;
};

type UsePasswordChangeFormArgs = {
  session: AuthSession | null;
  refreshUser?: () => Promise<unknown>;
};

export const usePasswordChangeForm = ({
  session,
  refreshUser,
}: UsePasswordChangeFormArgs): PasswordChangeFormState => {
  const { refreshSession } = useAuth();
  const passwordMutation = useChangePassword();
  const passwordError = useMutationErrorSnackbar();
  const { notify } = useNotifications();
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [passwordSuccess, setPasswordSuccess] = useState<string | null>(null);

  const currentPasswordError =
    currentPassword.length > 0 && !strongPasswordRegex.test(currentPassword);
  const currentPasswordHelperText = currentPasswordError
    ? 'Min 8 chars with upper, lower, number'
    : undefined;
  const newPasswordError = newPassword.length > 0 && !strongPasswordRegex.test(newPassword);
  const newPasswordHelperText = newPasswordError
    ? 'Min 8 chars with upper, lower, number'
    : undefined;
  const confirmPasswordError = confirmPassword.length > 0 && confirmPassword !== newPassword;
  const confirmPasswordHelperText = confirmPasswordError ? 'Passwords do not match' : undefined;

  const saveDisabled =
    passwordMutation.isPending ||
    !session ||
    !strongPasswordRegex.test(newPassword) ||
    confirmPassword !== newPassword ||
    !strongPasswordRegex.test(currentPassword) ||
    currentPassword === newPassword;

  const handleChangePassword = (): void => {
    if (!session) return;
    passwordError.clearError();
    setPasswordSuccess(null);
    if (!strongPasswordRegex.test(newPassword)) {
      passwordError.setError(
        new Error('Password must be at least 8 chars with upper, lower, number'),
      );
      return;
    }
    if (newPassword !== confirmPassword) {
      passwordError.setError(new Error('New passwords do not match'));
      return;
    }
    if (!strongPasswordRegex.test(currentPassword)) {
      passwordError.setError(
        new Error('Current password must be at least 8 chars with upper, lower, number'),
      );
      return;
    }
    passwordMutation.mutate(
      { id: session.userId, currentPassword, newPassword },
      {
        onSuccess: () => {
          setPasswordSuccess('Password updated');
          setCurrentPassword('');
          setNewPassword('');
          setConfirmPassword('');
          notify({ message: 'Password updated', tone: 'green' });
          passwordError.clearError();
          void Promise.all([refreshSession(), refreshUser?.()]);
        },
        onError: (err) => {
          passwordError.setError(err);
        },
      },
    );
  };

  return {
    currentPassword,
    newPassword,
    confirmPassword,
    showCurrentPassword,
    showNewPassword,
    showConfirmPassword,
    currentPasswordError,
    currentPasswordHelperText,
    newPasswordError,
    newPasswordHelperText,
    confirmPasswordError,
    confirmPasswordHelperText,
    saveDisabled,
    saving: passwordMutation.isPending,
    successMessage: passwordSuccess,
    onCurrentPasswordChange: setCurrentPassword,
    onNewPasswordChange: setNewPassword,
    onConfirmPasswordChange: setConfirmPassword,
    onToggleCurrentPassword: () => {
      setShowCurrentPassword((prev) => !prev);
    },
    onToggleNewPassword: () => {
      setShowNewPassword((prev) => !prev);
    },
    onToggleConfirmPassword: () => {
      setShowConfirmPassword((prev) => !prev);
    },
    onSave: handleChangePassword,
  };
};
